import { useState } from "react";
import { RelativeUrl } from "@adora/shared/utils/environment";
import post from "@adora/shared/utils/post";
import patch, { JsonValue } from "@adora/shared/utils/patch";
import { AdAssetData } from "../types/ad-asset";
import { AdAssetGroupData } from "../types/ad-asset-group";
import { filterEmptyValues } from "../features/ad-publishing/helpers";
import Result from "@adora/shared/types/result";
import { fetchBlobThroughDownloadApi } from "@adora/shared/utils/download";

interface AdAssetBulkFormData extends AdAssetData {
  brand_id: number;
  image_folder_path?: string;
  name: string;
  description: string;
  platforms: string[];
  tracking_id_start: number;
  google_ad_group_id?: string;
  meta_ad_set_id?: string;
  msan_ad_group_id?: string;
  theme_id?: number;
  google_campaign_name?: string;
  google_ad_group_name?: string;
  google_ad_group_url?: string;
  msan_campaign_name?: string;
  msan_ad_group_name?: string;
  msan_ad_group_url?: string;
  meta_campaign_name?: string;
  meta_ad_set_name?: string;
  meta_ad_set_url?: string;
  aspect_ratios?: string[];
}

interface CreateAdAssetGroupResponse {
  id: number;
  message: string;
}

const useMutateAdAssetGroup = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async (data: AdAssetBulkFormData): Promise<Result<CreateAdAssetGroupResponse>> => {
    setIsLoading(true);
    setError(null);

    const submissionData = {
      ...data,
      platforms: data.platforms,
      aspect_ratios: data.aspect_ratios
    };

    const bodyParams: JsonValue = filterEmptyValues(submissionData);
    const result = await post<CreateAdAssetGroupResponse>(`${RelativeUrl}/ad_asset_groups`, {}, bodyParams);

    setIsLoading(false);

    return result;
  };

  const deleteAdAssetGroup = async (id: number, brand_id: number): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    const result = await patch<{ success: boolean }>(
      `${RelativeUrl}/ad_asset_groups/${id}`,
      { brand_id: brand_id },
      { is_deleted: true }
    );

    setIsLoading(false);

    if (!result) {
      setError("Failed to delete ad asset group");
      return false;
    }

    return true;
  };

  const downloadAdAssetGroup = async (id: number, brandId: number): Promise<Blob | undefined> => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ brand_id: brandId.toString() });
      return await fetchBlobThroughDownloadApi(`${RelativeUrl}/ad_asset_groups/${id}/images/download`, params);
    } catch (error_) {
      const errorMessage =
        error_ instanceof Error
          ? `Failed to download ad asset group: ${error_.message}`
          : "Failed to download ad asset group: unknown error";
      console.error(errorMessage);
      setError(errorMessage);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  };

  const duplicateAdAssetGroup = async (brandId: number, id: number): Promise<Result<CreateAdAssetGroupResponse>> => {
    setIsLoading(true);
    setError(null);
    const result = await post<CreateAdAssetGroupResponse>(`${RelativeUrl}/ad_asset_groups/${id}/duplicate`, {
      brand_id: brandId
    });
    setIsLoading(false);
    return result;
  };

  const archiveAdAssetGroup = async (brandId: number, id: number): Promise<Result<AdAssetGroupData>> => {
    setIsLoading(true);
    setError(null);
    const result = await patch<AdAssetGroupData>(
      `${RelativeUrl}/ad_asset_groups/${id}`,
      {
        brand_id: brandId
      },
      { status: "archived" }
    );
    setIsLoading(false);
    return result;
  };

  const unarchiveAdAssetGroup = async (brandId: number, id: number): Promise<Result<AdAssetGroupData>> => {
    setIsLoading(true);
    setError(null);
    const result = await patch<AdAssetGroupData>(
      `${RelativeUrl}/ad_asset_groups/${id}`,
      {
        brand_id: brandId
      },
      { status: "ready_for_review", locked: false }
    );
    setIsLoading(false);
    return result;
  };

  return {
    create,
    deleteAdAssetGroup,
    downloadAdAssetGroup,
    duplicateAdAssetGroup,
    archiveAdAssetGroup,
    unarchiveAdAssetGroup,
    isLoading,
    error
  };
};

export default useMutateAdAssetGroup;
